// Englische Ressourcen — zugleich die Struktur-Referenz (Typ Dict). de.ts muss dieselbe
// Form erfüllen (TypeScript erzwingt das). Bewusst dependency-frei (Christian-Vorgabe):
// der Stringsatz ist klein und kontrolliert.
export const en = {
  app: {
    title: 'blackfortflow',
    tagline: 'Plant modelling, signal graphs and live monitoring',
  },
  nav: {
    site: 'Site',
    plant: 'Plant diagram',
    graph: 'Signal graph',
    monitor: 'Monitor',
    dashboard: 'Plant overview',
    training: 'Training',
    data: 'Data',
    history: 'History',
    audit: 'Audit',
    registrations: 'Approvals',
    settings: 'Settings',
  },
  site: {
    keinProjekt: 'No project selected',
    keinProjektHinweis: 'Pick a project above to see its site.',
    keinStandort: 'No site defined for this project',
    keinStandortHinweis:
      'The site view needs a centre point and a map source. Reference plants without a real location deliberately have none.',
    standortAnlegen: 'Define site',
    karteFehlt: 'The map is not part of this installation',
    stand: '{verortet} of {gesamt} plant items placed',
    offen: 'open',
    bearbeitenAn: 'Place items',
    bearbeitenAus: 'Finish placing',
    speichern: 'Save',
    gespeichert: 'Placement saved.',
    speichernFehler: 'The placement could not be saved.',
    erstWaehlen: 'Pick a plant item on the left, then a tool.',
    werkzeugHinweis: 'Pick a tool, then click on the map.',
    werkzeugSetzen: 'Place marker',
    werkzeugUmriss: 'Draw outline',
    werkzeugTrasse: 'Draw route',
    punkteGesetzt: '{anzahl} points',
    zeichnungFertig: 'Apply',
    koerper: 'Body shape',
    koerperZylinder: 'Cylinder (round basin, tank)',
    koerperQuader: 'Box (rectangular basin, building)',
    koerperMarke: 'Marker without volume',
    hoehe: 'Height in metres',
    groesse: 'Size in metres',
    umrissLoeschen: 'Remove outline',
    verortungLoesen: 'Clear placement',
  },
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
    close: 'Close',
    create: 'Create',
    noProject: 'No project selected',
    noProjectHint: 'Pick a project in the header or create a new one to begin.',
  },
  header: {
    language: 'Language',
  },
  user: {
    signedInAs: 'Signed in as {name}',
    logout: 'Sign out',
  },
  status: {
    project: 'Project: {name}',
    noProject: 'No active project',
    catalog: '{count} catalogue variables',
    graph: '{count} graph nodes',
  },
  domain: {
    biogas: 'Biogas',
    abwasser: 'Wastewater',
    mhkw: 'Waste-to-energy plant',
    brauerei: 'Brewery',
    molkerei: 'Dairy',
    baeder: 'Pools / thermal baths',
    fernwaerme: 'District heating',
    wasserwerk: 'Water works',
    oberflaeche: 'Surface treatment',
  },
  project: {
    label: 'Project',
    none: '— none —',
    new: 'New',
    newTitle: 'Create project',
    name: 'Name',
    namePlaceholder: 'e.g. Biogas North',
    domain: 'Domain',
    idPreview: 'ID',
    created: 'Project "{name}" created.',
    createFailed: 'Could not create the project.',
    listFailed: 'Could not load the project list.',
    loadFailed: 'Could not load the project data.',
  },
  plant: {
    palette: 'Symbols',
    domain: 'Domain',
    allDomains: 'All domains',
    search: 'Search symbols…',
    searchEmpty: 'No symbol matches the search.',
    category: {
      behaelter: 'Vessels',
      maschine: 'Machines',
      sensor: 'Sensors',
      aktor_sollwert: 'Actuators / setpoints',
      strom: 'Streams',
    },
    paletteEmpty: 'No symbols for this domain.',
    paletteFailed: 'Could not load the symbol palette.',
    canvasEmpty: 'Empty plant diagram',
    canvasHint: 'Pick a symbol from the palette to place it on the canvas.',
    properties: 'Properties',
    selectNode: 'No component selected',
    selectNodeHint: 'Select a sensor on the canvas to edit its variables.',
    noVariables: 'This component has no catalogue variables.',
    saveCatalog: 'Save catalogue',
    saveSchematic: 'Save diagram & catalogue',
    saved: 'Diagram & catalogue saved (rev {rev}).',
    saveFailed: 'Could not save the diagram/catalogue.',
    removeNode: 'Remove component',
    tag: 'Tag',
    unit: 'Unit',
    direction: 'Direction',
    input: 'Input (sensor)',
    output: 'Output (setpoint)',
    rangeMin: 'Range min',
    rangeMax: 'Range max',
    opcNode: 'OPC node id',
    opcType: 'OPC datatype',
    opcNamespace: 'OPC namespace URI',
    opcAccess: 'OPC access',
    opcQuality: 'OPC quality handling',
    opcUnset: '(unset)',
    opcAccessRead: 'Read',
    opcAccessWrite: 'Write',
    opcAccessReadWrite: 'Read + write',
    opcQualityGate: 'Gate',
    opcQualityPassthrough: 'Passthrough',
    opcQualityHoldLast: 'Hold last',
    stampDefaults: 'Insert control pattern',
    stampHint: 'Suggestion: pre-wired measurement conditioning / control loop for this component. Control loops start in manual (observing).',
    stamped: 'Control pattern inserted: {added} nodes added into the signal graph ({skipped} already present).',
    stampNothing: 'Control pattern already present — nothing added (idempotent).',
    stampFailed: 'Could not insert the control pattern.',
    stampNoVars: 'No catalogue variables on this component to stamp.',
    onboardingWizardOpen: 'Onboarding wizard',
    uaBrowseOpen: 'Browse OPC UA server (onboarding)',
    uaBrowse: {
      title: 'UA browse onboarding',
      intro:
        'Browse an OPC UA server read-only and adopt the discovered nodes as a signal catalogue draft — with OPC binding. Every candidate is a proposal; you confirm or discard it before it goes live.',
      endpoint: 'OPC UA endpoint',
      readOnlyNote:
        'Read-only, no blind scan: it browses from the Objects folder and writes nothing to the plant.',
      browse: 'Browse server',
      browsing: 'Browsing…',
      candidates: 'Discovered tag candidates',
      confirmedCount: '{confirmed} of {total} confirmed',
      confirmTag: 'Confirm tag “{tag}”',
      confidence: 'Detection confidence',
      empty: 'The server returned no variable nodes — check the endpoint/permissions.',
      openPoints: 'Still to set in review',
      confirm: 'Adopt {confirmed} tag(s)',
      saving: 'Saving…',
      saved: 'Signal catalogue written (rev {rev}).',
      cancel: 'Cancel',
    },
  },
  signal: {
    catalogNodes: 'Catalogue & model',
    blocks: 'Block types',
    superblocks: 'Superblocks',
    superblockNotRunnable: 'not runnable (contains not-yet-mapped blocks)',
    superblockInner: 'Superblock',
    superblockInnerFailed: 'Could not load the inner graph.',
    superblockRoot: 'Main graph',
    superblockBack: 'Back',
    superblockDrillHint: 'Double-click opens the inner graph of “{name}”.',
    importSuperblockButton: 'Import superblock (.SBL)',
    importSuperblockBusy: 'Importing…',
    importSuperblockGroup: 'Imported',
    importSuperblockRemove: 'Remove imported superblock',
    importSuperblockDone: '{imported} superblock(s) imported.',
    importSuperblockPartial: '{imported} imported, {skipped} skipped: {reasons}',
    importSuperblockNone: 'No file imported — {reasons}',
    importSuperblockFailed: 'The superblock import failed.',
    importSuperblockRemoved: 'Imported superblock “{ref}” removed.',
    importSuperblockRemoveFailed: 'Could not remove the superblock.',
    unknownSuperblocks: 'Unknown superblocks',
    unknownSuperblocksHint:
      'This graph references {count} superblock(s) not in the library. Bulk-import the missing .SBL files.',
    bulkImportOpen: 'Bulk-import missing superblocks',
    bulkImport: {
      title: 'Bulk-import superblocks (.SBL)',
      intro:
        'Select the .SBL files. Nested superblocks resolve over several passes — after each import you see what is still missing.',
      progress: '{resolved} of {total} resolved',
      stillMissing: 'Still missing',
      allResolved: 'All referenced superblocks are resolved.',
      skipped: 'Skipped',
      select: 'Choose .SBL files',
      selectMore: 'Add more .SBL files',
      busy: 'Importing…',
      later: 'Later',
    },
    search: 'Search blocks/superblocks…',
    searchEmpty: 'No block matches the search.',
    palette: 'Blocks',
    paletteFailed: 'Could not load the block palette.',
    canvasEmpty: 'Empty signal graph',
    canvasHint: 'Add catalogue nodes or block types, then wire their ports.',
    properties: 'Properties',
    selectNode: 'No node selected',
    selectNodeHint: 'Select a node on the canvas to edit its parameters.',
    save: 'Save graph',
    saved: 'Graph saved (rev {rev}).',
    saveFailed: 'Could not save the graph.',
    validate: 'Validate',
    valid: 'Graph is valid.',
    invalid: 'Graph has findings — see the list.',
    validateFailed: 'Could not validate the graph.',
    findings: 'Findings',
    tag: 'Catalogue tag',
    chooseTag: '— choose tag —',
    filterFromCatalog:
      'Filter kind and time constant live on the signal in the signal catalogue — change them there so the same smoothing applies in every graph. For graph-local smoothing use the PT1 block.',
    model: 'Model',
    chooseModel: '— choose model —',
    modelVersion: 'Version',
    chooseVersion: '— choose version —',
    noModels: 'No models in the registry yet.',
    modelsFailed: 'Could not load the model registry.',
    params: 'Parameters',
    noParams: 'No parameters yet.',
    newParam: 'New parameter',
    removeNode: 'Remove node',
      kind: {
        input: 'Measurements',
        output: 'Actuator values',
        setpoint: 'Setpoints',
        disturbance: 'Disturbances',
        exogenous: 'Exogenous (forecast)',
        health: 'Condition signals',
      },
    node: {
      'catalog-input': 'Catalogue input',
      'catalog-output': 'Catalogue output',
      'signal-filter': 'Signal smoothing',
      'mpc-optimizer': 'MPC optimizer',
      'model-inference': 'Model inference',
    },
  },
  boris: {
    button: 'Import BORIS',
    title: 'Import a BORIS system file',
    intro:
      'Upload a BORIS system file (.BSY) together with its referenced superblocks (.SBL). This '
      + 'derives a signal graph and a signal catalogue; open input terminals become catalogue '
      + 'inputs, internal sources are kept. The instance runs as soon as the compute path is '
      + 'complete.',
    files: 'Files',
    chooseBsy: 'Choose .BSY',
    noBsy: 'no .BSY selected',
    chooseSbl: 'Add .SBL',
    noSbl: 'no .SBL',
    sblCount: '{count} superblock file(s)',
    target: 'Target',
    targetNew: 'Create a new project',
    newName: 'Project name',
    newNamePlaceholder: 'e.g. HRT import',
    targetCurrent: 'Into current project ({project})',
    targetCurrentNone: 'Into current project (none open)',
    import: 'Import',
    importing: 'Importing…',
    importFailed: 'The BORIS import failed.',
    runnable: 'Compute path complete — the instance is runnable.',
    notRunnable:
      'Compute path incomplete — unsupported blocks or unresolved superblocks remain as '
      + 'placeholders. Fill in the missing parts to make them run.',
    supported: 'imported',
    unsupported: 'placeholders',
    unresolved: 'unresolved SB',
    placeholders: 'Unsupported blocks (placeholders)',
    unresolvedSuperblocks: 'Unresolved superblocks',
    importedInto: 'Imported into project “{project}”.',
    done: 'Done',
    notifyRunnable: 'BORIS system imported into “{project}” — runnable.',
    notifyPartial: 'BORIS system imported into “{project}” — with placeholders (see report).',
  },
  modbusMap: {
    button: 'Import Modbus register map',
    title: 'Import a Modbus register map',
    intro:
      'Register maps are often undocumented. Upload the operator/vendor map (CSV/table) instead '
      + 'of guessing or scanning the plant. Each row becomes a low-confidence register-to-tag '
      + 'proposal that you confirm or discard below. The mapping is read-only — no write path, no '
      + 'address-space scan, no packet is sent to the plant.',
    choose: 'Choose CSV/table file',
    chooseAnother: 'Choose another file',
    importing: 'Reading map…',
    importFailed: 'The register map could not be read.',
    noProposals: 'The map produced no rows.',
    disclaimerTitle: 'Read-only import — no scan',
    tableCaption: 'Register → tag proposals',
    colDecision: 'Take over',
    colTag: 'Tag',
    colRegister: 'Register',
    colDatatype: 'Data type',
    colWordOrder: 'Word order',
    colScaling: 'Scaling',
    colUnit: 'Unit',
    colConfidence: 'Confidence',
    colIssues: 'Notes',
    confirm: 'Confirm mapping',
    confirmBusy: 'Writing…',
    confirmFailed: 'The mapping could not be applied.',
    confirmed: 'Mapping confirmed — {count} tag(s) written (rev {rev}).',
    incompleteHint: 'Every row must be confirmed or discarded before writing.',
    notBindable: 'not bindable',
    selectAll: 'Take over all',
    discardAll: 'Discard all',
    reviewNote: 'Every row must be reviewed — nothing goes live unchecked.',
    close: 'Close',
    cancel: 'Cancel',
    editTag: 'Edit tag',
  },
  onboardingWizard: {
    title: 'Onboarding wizard',
    stepName: {
      tier: 'Method',
      discovery: 'Discovery',
      review: 'Proposal & confirmation',
      authorize: 'Authorization',
    },
    tierIntro:
      'Guided onboarding: choose the connection method. In both cases nothing is written to the plant — discovery is requested and read-only, every candidate is a proposal, and the catalogue is written only once a written authorization has been captured.',
    tierA: 'Tier A — OPC UA',
    tierADesc: 'Browse an OPC UA server read-only and propose tag candidates with OPC binding.',
    tierB: 'Tier B — Modbus',
    tierBDesc: 'Import a register map (CSV/vendor table) and propose register→tag bindings.',
    endpoint: 'OPC UA endpoint',
    readOnlyNoteA:
      'Read-only, no blind scan: it browses from the Objects folder and writes nothing to the plant.',
    readOnlyNoteB:
      'Import, not sweep: the register map is read — no address-space scan, no packet to the plant.',
    browse: 'Browse server',
    discovering: 'Discovering…',
    chooseFile: 'Choose register map',
    importing: 'Importing…',
    candidates: 'Discovered candidates',
    confirmedCount: '{confirmed} of {total} confirmed',
    confirmTag: 'Confirm candidate “{tag}”',
    confidence: 'Detection confidence',
    notBindable: 'not bindable',
    empty: 'No candidates found — check the endpoint/permissions or the register map.',
    toAuthorize: 'Continue to authorization (adopt {confirmed})',
    authorizeIntro:
      'Capture the written authorization. It is written to the audit trail in a tamper-evident way and bound to the written catalogue revision.',
    authorizeSummary: 'Method: {tier} · adopt {confirmed} candidate(s).',
    grantedBy: 'Authorized by',
    grantedByPlaceholder: 'Name/ID of the approver',
    grantedAt: 'Time of authorization',
    scope: 'Scope (optional)',
    scopePlaceholder: 'e.g. “Onboarding aeration tank, 12 tags”',
    back: 'Back',
    confirmAndWrite: 'Authorize & write catalogue',
    saving: 'Saving…',
    saved: 'Signal catalogue written (rev {rev}).',
    auditNote: 'The authorization was recorded in the audit trail in a tamper-evident way.',
    cancel: 'Cancel',
  },
  monitor: {
    start: 'Start run',
    startLive: 'Start live',
    stop: 'Stop run',
    started: 'Run {run} started.',
    startFailed: 'Could not start the run.',
    stopped: 'Run stopped.',
    stopFailed: 'Could not stop the run.',
    modeSim: 'Simulation',
    modeLive: 'Live (OPC demonstrator)',
    mode: {
      label: 'Run mode',
    },
    live: {
      endpoint: 'OPC endpoint',
      noEndpoint: 'Set the OPC endpoint in the settings to run live.',
      started: 'Live run {run} started.',
      failed: 'Could not start the live run.',
    },
    attached: 'Live run active (read-only)',
    reconnect: 'Reconnect',
    lost: 'Connection lost.',
    trend: 'Trend',
    xy: 'XY plot',
    chart: {
      trend: 'Trend',
      xy: 'XY',
    },
    values: 'Values',
    noData: 'No live data yet',
    noDataHint: 'Start a run to stream tick values over the WebSocket.',
    noValues: 'No values yet.',
    paceFailed: 'Could not change the speed.',
    speed: {
      heading: 'Speed',
      cycle: 'Cycle time',
      factor: 'Time-lapse',
      realtime: 'Real time',
      locked: 'Live OPC: real time (1×), time-lapse locked',
    },
    view: {
      window: 'Time window',
      all: 'All',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      reset: 'Reset',
      follow: 'Follow latest',
    },
    conn: {
      idle: 'Idle',
      connecting: 'Connecting…',
      live: 'Live',
      lost: 'Lost',
    },
    csv: {
      source: 'Source',
      sim: 'Simulation',
      replay: 'From CSV (replay)',
      chooseSource: '— choose data source —',
      noSources: 'No data sources yet — add one in the Data view.',
      endBehavior: 'At end of file',
      loopCount: 'Repeats (0 = unlimited)',
      loopGap: 'Gap between repeats (s)',
      end: {
        stop: 'Stop',
        loop: 'Loop',
        hold: 'Hold last value',
      },
    },
  },
  training: {
    heading: 'Training environment',
    intro:
      'Train soft sensors in the notebooks, export them to ONNX, publish them to the model registry and use them as a model-inference block in the signal graph.',
    chain: 'Notebook → training → ONNX + manifest → registry → block in the signal graph.',
    openRegistry: 'Open model registry',
    goals: {
      heading: 'Optimisation goals for training',
      intro:
        'Which goals of the MPC optimiser the training should pursue — and why. The selectable goals come from the mpc-optimizer nodes of the signal graph; selection and rationale belong to the plant model and are git-versioned.',
      auswahl: 'Goals',
      begruendung: 'Rationale for the selection',
      begruendungHinweis:
        'Whoever asks in two years why biology outweighed yield will find the answer here instead of in a meeting note.',
      speichern: 'Save goal parameters',
      trainingStarten: 'Start training',
      notebookGenerieren: 'Generate notebook',
      notebookAusfuehren: 'Run generated notebook',
      erzeugt: 'Notebook generated with {model}. Please read the code before it runs.',
      vorschauHinweis: 'Generated with {model}, stored as snapshot {sha}… — generated code is not reviewed code.',
      laufOk: 'Run succeeded.',
      laufFehler: 'Run failed.',
      gespeichert: 'Goal parameters saved.',
      gestartet: 'Training run {id} started.',
      ungespeichert: 'Unsaved changes — save before starting.',
      begruendungPflicht: 'Goals selected but not justified — a rationale is mandatory for a training run.',
      laden: 'Loading goal parameters …',
      keineZiele:
        'This plant graph carries no mpc-optimizer node. There is nothing to select — add the node in the signal graph.',
      einklappen: 'Collapse',
      ausklappen: 'Expand',
      gewicht: 'weight',
      art: {
        setpoint: 'hold setpoint {wert}',
        min: 'minimise',
        max: 'maximise',
      },
    },
    loading: 'Checking the training environment…',
    unavailableTitle: 'Training environment not started',
    unavailableHint:
      'The embedded JupyterLab is not reachable. Start it as described in the operations documentation (demo/klaeranlage), then reload this view.',
    disabledTitle: 'Training environment disabled',
    disabledHint:
      'This installation does not offer the embedded training environment. Enable it via BFF_TRAINING_ENABLED in the backend configuration.',
    frameTitle: 'JupyterLab training environment',
    openInTab: 'Open in a new tab',
    pod: {
      label: 'GPU / training pod',
      local: 'Local (CPU)',
      active: 'active',
      inactive: 'off',
      provider: 'Provider',
      account: 'Account',
      gpu: 'GPU',
      region: 'Region',
      cloudType: 'Cloud type',
      noneChosen: '— not set —',
      secrets: 'credentials configured',
      noSecrets: 'no credentials',
      none: 'No GPU pod configured (local CPU path).',
      configure: 'Configure GPU pod',
      editorTitle: 'My GPU / training pod',
      editorHint:
        'This configuration is yours alone: provider, GPU and cost run on your account. It applies to your training runs, not to anyone else’s.',
      owner: 'Owner',
      enabledLabel: 'Use pod',
      gpuType: 'GPU type',
      gpuCount: 'GPU count',
      cpuCores: 'CPU cores (max.)',
      memoryGb: 'Memory GB (max.)',
      maxRuntimeMin: 'Runtime minutes (max.)',
      maxCostEur: 'Cost cap EUR',
      apiKeyHandle: 'Credentials (OpenBao handle)',
      apiKeyHint:
        'The reference only, never the key: bao:<path>#<field>. The value is read from OpenBao when a pod starts.',
      handleInvalid:
        'That is not an OpenBao handle. Expected bao:<path>#<field> — a plaintext key does not belong here.',
      save: 'Save',
      saving: 'Saving…',
      saved: 'Saved.',
      saveFailed: 'The pod configuration could not be saved.',
      loadFailed: 'The pod configuration could not be loaded.',
      close: 'Close',
    },
  },
  registry: {
    title: 'Model registry',
    hint: 'Published models with their versions, governance status and the active version. Pick a version to see its manifest (inputs/outputs, metrics, notebook hash, approval).',
    reload: 'Reload',
    close: 'Close',
    loading: 'Loading the registry…',
    loadFailed: 'The model registry could not be loaded.',
    empty: 'No models yet',
    emptyHint: 'Train a model in the notebooks, export it to ONNX and publish it to the registry — then it shows up here.',
    pickVersion: 'Pick a version to see its manifest.',
    manifestLoading: 'Loading the manifest…',
    manifestFailed: 'The manifest could not be loaded.',
    active: 'active',
    currentIs: 'Active: {version}',
    noCurrent: 'No active version',
    inputs: 'Inputs',
    outputs: 'Outputs',
    noPorts: 'No ports in the manifest.',
    portName: 'Name',
    portQuantity: 'Quantity',
    portUnit: 'Unit',
    portDtype: 'Type',
    metrics: 'Metrics',
    notebookHash: 'Notebook hash',
    governance: 'Approval',
    approvedBy: 'Approved by',
    approvedAt: 'Approved at',
    note: 'Note',
    approve: 'Approve',
    activate: 'Set active',
    approveConfirm: 'Approve version {version} of “{model}” (status → approved)?',
    activateConfirm: 'Set version {version} of “{model}” active (becomes the version in use)?',
    approved: 'Version {version} of “{model}” approved.',
    activated: 'Version {version} of “{model}” is now active.',
    approveFailed: 'Approval failed.',
    activateFailed: 'Activation failed.',
    status: {
      draft: 'Draft',
      approved: 'Approved',
      active: 'Active',
      retired: 'Retired',
      deprecated: 'Deprecated',
    },
  },
  dashboard: {
    overview: 'Plant overview',
    loops: 'Control loops',
    noLoops: 'No actuator setpoints in this catalogue.',
    empty: 'No catalogue to monitor',
    emptyHint: 'This project has no signal catalogue yet. Add sensors and setpoints in the plant view.',
    allNormal: 'All values normal',
    alarms: 'alarms',
    warnings: 'warnings',
    setpoint: 'Setpoint',
    clamped: 'Clamped',
    rateLimited: 'Rate limited',
    state: {
      warn: 'Warning',
      alarm: 'Alarm',
    },
    mode: {
      auto: 'Auto',
      hand: 'Manual',
      failsafe: 'Fail-safe',
      unknown: 'Unknown',
    },
    display: {
      label: 'Display',
      all: 'All',
      perTile: 'Display type of this tile',
      saveFailed: 'Could not save the display type.',
      numeric: 'Tile',
      gauge: 'Gauge',
      trend: 'Trend',
      bar: 'Bar',
      lamp: 'Lamp',
      digital: 'Digital',
      table: 'Table',
    },
    table: {
      tag: 'Tag',
      name: 'Name',
      value: 'Value',
      unit: 'Unit',
      state: 'State',
    },
    tableState: {
      normal: 'Normal',
      warn: 'Warning',
      alarm: 'Alarm',
      unknown: 'Unknown',
    },
    watchdog: {
      label: 'Watchdog',
      ok: 'OK',
      tripped: 'Tripped',
      unknown: 'Unknown',
    },
  },
  data: {
    heading: 'Data sources',
    intro:
      'Upload recorded CSV data and map its columns to catalogue inputs. Only the mapping is versioned with the project — the CSV files stay on this instance and are not shipped with the appliance.',
    files: 'CSV files',
    upload: 'Upload CSV',
    uploading: 'Uploading…',
    uploaded: 'File "{name}" uploaded.',
    uploadFailed: 'Could not upload the file.',
    noFiles: 'No CSV files yet. Upload one to begin.',
    remove: 'Remove',
    removed: 'File removed.',
    removeFailed: 'Could not remove the file.',
    selectFile: 'Select a file to view its columns and mapping.',
    columns: 'Columns',
    sample: 'Sample rows',
    timestampColumn: 'Timestamp column',
    noTimestamp: '— none (one row per tick) —',
    mapping: 'Column → input mapping',
    mappingHint:
      'Assign each CSV column to a catalogue input (or leave it unassigned). The timestamp column drives the tick; between samples the value is held.',
    csvColumn: 'CSV column',
    catalogInput: 'Catalogue input',
    unassigned: '— unassigned —',
    noInputs: 'This project has no catalogue inputs. Add sensors in the plant view first.',
    save: 'Save mapping',
    saved: 'Mapping saved (rev {rev}).',
    saveFailed: 'Could not save the mapping.',
    loadFailed: 'Could not load the data sources.',
    columnsFailed: 'Could not read the columns of this file.',
    empty: 'No project selected',
    emptyHint: 'Pick a project to manage its CSV data sources.',
  },
  history: {
    title: 'Change history',
    hint: 'Every project change is a git commit. Click a row to load its diff; roll back to any state as a new commit.',
    empty: 'No project selected',
    emptyHint: 'Pick a project to review its change history.',
    noCommits: 'No commits yet.',
    loadFailed: 'Could not load the history.',
    diffFailed: 'Could not load the diff.',
    diffEmpty: 'No textual change for this object.',
    objects: 'Changed objects',
    loadMore: 'Load more',
    revert: 'Roll back to this state',
    reverting: 'Rolling back…',
    revertConfirm: 'Roll the project back to revision {rev}? This creates a new commit; the forward history is kept.',
    reverted: 'Rolled back to {rev} (new revision {newRev}).',
    revertFailed: 'Could not roll back.',
    author: 'Author',
  },
  audit: {
    title: 'Audit trail',
    hint: 'The tamper-evident event chain of this project. The badge shows whether the hash chain is intact.',
    empty: 'No project selected',
    emptyHint: 'Pick a project to review its audit trail.',
    noEntries: 'No audit entries yet.',
    loadFailed: 'Could not load the audit trail.',
    chainOk: 'Chain intact',
    chainBroken: 'Chain broken at seq {seq}',
    length: '{count} entries',
    export: 'Export audit',
  },
  login: {
    title: 'blackfortflow',
    subtitlePassword: 'Sign in with your username and password.',
    subtitleToken: 'Sign in — paste your access token.',
    username: 'Username',
    password: 'Password',
    token: 'Access token',
    submit: 'Sign in',
    withPassword: 'Sign in with password',
    withToken: 'Sign in with token',
    toRegister: 'Create an account',
    forgot: 'Forgot your password?',
    failed: 'Sign-in failed. Check your username and password.',
    error: {
      email_unverified: 'Please confirm your email address first. Check your inbox for the link.',
      approval_pending: 'Your account is waiting for approval by an administrator.',
      account_rejected: 'Your registration was declined. Please contact an administrator.',
      account_disabled: 'Your account is disabled. Please contact an administrator.',
      account_not_active: 'Your account is not active yet.',
      too_many_attempts: 'Too many sign-in attempts. Please try again later.',
    },
  },
  auth: {
    backToLogin: 'Back to sign-in',
  },
  mfa: {
    subtitle: 'Two-factor authentication',
    hint: 'Enter the 6-digit code from your authenticator app.',
    recoveryHint: 'Enter one of your recovery codes.',
    codePlaceholder: '6-digit code',
    recoveryPlaceholder: 'Recovery code',
    submit: 'Verify',
    useRecovery: 'Use a recovery code instead',
    useAuthenticator: 'Use the authenticator app instead',
    error: {
      invalid: 'The code is not valid. Please try again.',
      expired: 'The second step expired. Please sign in again.',
    },
  },
  register: {
    subtitle: 'Create an account. You will receive a confirmation email.',
    email: 'Email',
    password: 'Password',
    name: 'Display name (optional)',
    submit: 'Register',
    submitted:
      'Registration received. Please confirm your email address via the link we sent you.',
    conflict: 'An account with this username or email already exists.',
    invalid: 'Please enter a valid email address and a password.',
    disabled: 'Self-registration is not available on this instance.',
    failed: 'Registration failed. Please try again later.',
  },
  forgot: {
    subtitle: 'Enter your email to receive a link to reset your password.',
    email: 'Email',
    submit: 'Send reset link',
    sent: 'If an account exists for this email, a reset link has been sent.',
    tooMany: 'Too many requests. Please try again later.',
    failed: 'Could not request a password reset. Please try again later.',
  },
  reset: {
    subtitle: 'Set a new password for your account.',
    identity: 'Username',
    token: 'Reset token',
    newPassword: 'New password',
    submit: 'Set new password',
    done: 'Password set. Please sign in with your new password.',
    invalid: 'The reset link is invalid, expired or already used.',
    failed: 'Could not set the new password. Please try again.',
  },
  verify: {
    subtitle: 'Confirm your email address',
    hint: 'Confirming your email address…',
    checking: 'Confirming…',
    done: 'Email confirmed. Your account is now waiting for approval by an administrator.',
    failed: 'The confirmation link is invalid or expired.',
    missing: 'The confirmation link is incomplete (missing token).',
    resend: 'Resend confirmation email',
    resent: 'If a pending account exists, the confirmation email was sent again.',
    resendTooMany: 'Too many requests. Please wait a moment before requesting again.',
  },
  registrations: {
    heading: 'Registration approvals',
    intro: 'Accounts waiting for approval. Approve to activate sign-in, or decline permanently.',
    rolesLabel: 'Initial roles',
    reason: 'Reason (optional)',
    reasonPlaceholder: 'e.g. no valid business context',
    approve: 'Approve',
    reject: 'Decline',
    rejectConfirm: 'Confirm decline',
    approved: 'Account “{identity}” approved — the user can sign in now.',
    rejected: 'Account “{identity}” declined.',
    notPending: 'This account was already decided. The list has been refreshed.',
    approveFailed: 'Could not approve the account.',
    rejectFailed: 'Could not decline the account.',
    loadFailed: 'Could not load the approval queue.',
    empty: 'No accounts waiting for approval.',
    emptyHint: 'New self-registrations appear here once their email is confirmed.',
  },
  settings: {
    heading: 'Settings',
    tabUsers: 'Users',
    tabFourEyes: 'Four eyes',
    tabLlm: 'LLM',
    tabProfile: 'My profile',
    // ── Project settings (OPC endpoint + cycle time, Issue #121) ──
    opcEndpoint: 'OPC endpoint',
    cycleTime: 'Cycle time (ms)',
    project: {
      heading: 'Project settings',
      save: 'Save settings',
      saved: 'Project settings saved.',
      saveFailed: 'Could not save the project settings.',
      loadFailed: 'Could not load the project settings.',
      readOnly: 'Read-only (no edit right).',
      notSet: 'not set',
    },
    // ── User management ──
    usersHeading: 'User management',
    usersIntro: 'Create users, assign roles, reset passwords and rotate tokens.',
    identity: 'Identity',
    roles: 'Roles',
    statusColumn: 'Status',
    actions: 'Actions',
    active: 'Active',
    disabled: 'Disabled',
    hasPassword: 'Password login',
    hasToken: 'Token active',
    noUsers: 'No users yet.',
    loadUsersFailed: 'Could not load the user list.',
    // Create
    createHeading: 'Create user',
    createIdentity: 'Identity',
    createIdentityPlaceholder: 'e.g. m.mueller',
    initialPassword: 'Initial password',
    initialPasswordPlaceholder: 'optional',
    create: 'Create user',
    created: 'User "{identity}" created.',
    createFailed: 'Could not create the user.',
    // Roles
    editRoles: 'Edit roles',
    saveRoles: 'Save roles',
    rolesSaved: 'Roles for "{identity}" updated.',
    updateFailed: 'Could not update the user.',
    lastAdmin: 'This user is the last active admin and cannot be changed or removed.',
    // Password reset
    resetPassword: 'Reset password',
    newPassword: 'New password',
    setPassword: 'Set password',
    passwordSet: 'Password for "{identity}" set.',
    setPasswordFailed: 'Could not set the password.',
    // Token
    rotateToken: 'Rotate token',
    tokenRotated: 'New token for "{identity}" — shown once, copy it now:',
    rotateFailed: 'Could not rotate the token.',
    copyToken: 'Copy',
    tokenCopied: 'Token copied.',
    // Delete
    remove: 'Delete',
    confirmRemove: 'Really delete user "{identity}"?',
    removed: 'User "{identity}" deleted.',
    removeFailed: 'Could not delete the user.',
    // Profile
    profileHeading: 'My profile',
    myIdentity: 'Identity',
    myRoles: 'Roles',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    changePasswordButton: 'Change password',
    passwordChanged: 'Your password was changed.',
    changePasswordFailed: 'Could not change your password.',
    loadMeFailed: 'Could not load your profile.',
    // Two-factor / TOTP (Issue #192)
    mfaHeading: 'Two-factor authentication',
    mfaIntro:
      'Protect your account with a second factor from an authenticator app (TOTP). After the password you will be asked for a 6-digit code.',
    mfaStatusOn: 'Two-factor is active.',
    mfaStatusOff: 'Two-factor is off.',
    mfaRecoveryLeft: '{count} recovery codes left.',
    mfaEnable: 'Set up two-factor',
    mfaEnrollHint:
      'Scan the QR code with your authenticator app, or enter the secret manually. Then confirm with the first 6-digit code.',
    mfaSecretLabel: 'Secret (manual entry)',
    mfaConfirmCode: 'Code from the app',
    mfaConfirm: 'Confirm and activate',
    mfaEnabledNotice: 'Two-factor is now active.',
    mfaRecoveryHeading: 'Recovery codes',
    mfaRecoveryIntro:
      'Store these codes safely. Each one works once and lets you sign in if you lose your authenticator. They are shown only now.',
    mfaRecoveryDone: 'I saved my codes',
    mfaDisable: 'Turn off two-factor',
    mfaDisableConfirmPassword: 'Confirm with your current password',
    mfaDisabled: 'Two-factor was turned off.',
    mfaCancel: 'Cancel',
    mfaEnrollFailed: 'Could not start the setup.',
    mfaActivateFailed: 'The code is not valid. Please try again.',
    mfaDisableFailed: 'Could not turn off two-factor. Check your password.',
    mfaLoadFailed: 'Could not load the two-factor status.',
    // Access
    forbidden: 'Admin rights are required for user management.',
    // Four eyes per project (Issue #114)
    fourEyesHeading: 'Four eyes per project',
    fourEyesIntro:
      'Default: off — one person may propose and approve. When enabled, a second person is required to approve the deploy.',
    fourEyesProject: 'Project',
    fourEyesColumn: 'Four-eyes principle',
    fourEyesOn: 'On',
    fourEyesOff: 'Off',
    fourEyesToggle: 'Toggle four-eyes principle for "{project}"',
    llm: {
      heading: 'LLM configuration',
      intro:
        'Access to the language-model provider for the notebook builder. The key is never stored in clear text: either as an OpenBao handle (bao:<path>#<field>) or encrypted server-side. Resolved project → user → appliance; the first level carrying a key wins.',
      keineVerschluesselung:
        'No encryption key is configured on this server. A key typed here will be rejected — store an OpenBao handle (bao:<path>#<field>) instead.',
      projektStand: 'Stored in the project',
      keinSchluessel: 'no key',
      artBao: 'OpenBao handle',
      artEnc: 'stored encrypted',
      effektiv: 'Effective: model {model} (from {modelFrom}), key from {keyFrom}',
      ebene: {
        projekt: 'project',
        nutzer: 'user',
        server: 'appliance',
        default: 'default',
      },
      schluessel: 'API key (replace)',
      schluesselPlatzhalter: 'key or bao:<path>#<field>',
      modell: 'Model',
      modellErben: '— inherit from next level —',
      speichern: 'Save LLM configuration',
      entfernen: 'Remove stored key',
      entfernt: 'Key removed.',
      gespeichert: 'LLM configuration saved.',
      laden: 'Loading LLM configuration …',
      keinProjekt: 'The LLM access belongs to a project. Select a project at the top left to configure it.',
    },
    fourEyesNoProjects: 'No projects yet.',
    fourEyesLoadFailed: 'Could not load the projects.',
    fourEyesSaveFailed: 'Could not change the four-eyes principle.',
    role: {
      admin: 'Admin',
      betreiber: 'Operator (Betreiber)',
      data_scientist: 'Data scientist',
      deployer: 'Deployer',
      operator: 'Operator',
    },
  },
} as const;

// Dict ist der strukturelle Vertrag beider Sprachdateien: dieselbe Baumform wie en,
// aber jedes Blatt darf ein beliebiger String sein (rekursiv, beliebig tief).
type Deep<T> = { [K in keyof T]: T[K] extends string ? string : Deep<T[K]> };
export type Dict = Deep<typeof en>;
